import React from "react";
import Paper from "@components/Paper/Paper";
import PaperItem from "@components/Paper/PaperItem";
import {
  isBallastVesselCondition,
  mapApiCargoOnboardToForm,
  mapCargoOnboardItemsToGrid,
} from "@features/vessel-call/cargoOnboard";
import CargoOnboardGrid from "@features/vessel-call/components/CargoOnboardGrid";
import LastPortsTable from "@features/vessel-call/vessel-call-status/VesselCallDetails/LastPortsTable.tsx";
import PreArrivalCertificates from "@features/vessel-call/vessel-call-status/VesselCallDetails/PreArrivalCertificates.tsx";
import type { VesselCallNotificationDTO } from "@features/vessel-call/vessel-call-status/VesselCallStatus.types.tsx";
import { t } from "i18next";
import {
  getNatureOfCallSubSectionItems,
  getPortSubSectionItems,
  getPreArrivalCertificates,
  getPrimaryNatureOfCallSubSectionItems,
  getSolasIspsSubSectionItems,
  getVoyageSubSectionItems,
} from "./utils";

const TEST_ID_BASE = "vessel-call-status-page";
const LANG_BASE = "vessel-call";
const transStyleClass = "font-normal text-[#646A77]";
const wrapperStyleClass = "border-t border-[#F1F2F4] pt-3 pb-3 !mb-0 !pb-0";
const wrapperFirstStyleClass = "pt-3 !mb-0 !pb-0";

const VesselPreArrivalNotification: React.FC<{
  vesselDetails?: VesselCallNotificationDTO | null;
}> = ({ vesselDetails }) => {
  const certificates = getPreArrivalCertificates(vesselDetails);
  const cargoItems = mapCargoOnboardItemsToGrid(
    mapApiCargoOnboardToForm(vesselDetails?.cargoOnboard),
  );
  const showCargoOnboard = !isBallastVesselCondition(vesselDetails?.vesselCondition);

  return (
    <div
      data-testid={`${TEST_ID_BASE}-prearrival`}
      className="flex flex-col w-full h-full p-8 px-1 gap-6"
    >
      <Paper variant="outlined">
        <p className="font-bold">{t(`${LANG_BASE}.PreArrivalNotification.port`)}</p>
        <div className="grid grid-cols-2 gap-x-10">
          {getPortSubSectionItems({
            vesselDetails,
            TEST_ID_BASE,
            LANG_BASE,
            t,
            transStyleClass,
            wrapperStyleClass,
            wrapperFirstStyleClass,
          })
            .flat()
            .map(({ testId, translate, text, textStyles, tranStyle, wrapperStyle }) => (
              <PaperItem
                key={testId}
                testId={testId}
                translate={translate}
                textStyles={textStyles}
                tranStyle={tranStyle}
                wrapperStyle={wrapperStyle}
                text={text}
              />
            ))}
        </div>
      </Paper>
      <Paper variant="outlined">
        <p className="font-bold">{t(`${LANG_BASE}.PreArrivalNotification.voyage-details`)}</p>
        <div className="display:block">
          {getVoyageSubSectionItems({
            vesselDetails,
            TEST_ID_BASE,
            LANG_BASE,
            t,
            transStyleClass,
            wrapperStyleClass,
            wrapperFirstStyleClass,
          }).map((row: any[], idx: number) => (
            <div className="flex flex-row gap-10" key={idx}>
              {row.map((item: any) => {
                const { testId, translate, text, textStyles, tranStyle, wrapperStyle } = item;
                return (
                  <PaperItem
                    key={testId}
                    testId={testId}
                    translate={translate}
                    textStyles={textStyles}
                    tranStyle={tranStyle}
                    wrapperStyle={wrapperStyle}
                    text={text}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </Paper>
      {showCargoOnboard && (
        <Paper variant="outlined">
          <p className="font-bold mb-4">{t(`${LANG_BASE}.PreArrivalNotification.cargo-onboard`)}</p>
          <CargoOnboardGrid items={cargoItems} testId={`${TEST_ID_BASE}-cargo-grid`} />
        </Paper>
      )}
      <Paper variant="outlined">
        <p className="font-bold">{t(`${LANG_BASE}.PreArrivalNotification.nature-of-call`)}</p>
        <div className="flex flex-col">
          {getPrimaryNatureOfCallSubSectionItems({
            vesselDetails,
            TEST_ID_BASE,
            LANG_BASE,
            t,
            wrapperFirstStyleClass,
            transStyleClass,
          }).map(({ testId, translate, text, textStyles, wrapperStyle, tranStyle }) => (
            <div className="flex flex-col w-1/2" key={testId}>
              <PaperItem
                testId={testId}
                translate={translate}
                textStyles={textStyles}
                wrapperStyle={wrapperStyle}
                tranStyle={tranStyle}
                text={text}
              />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-x-8">
          {getNatureOfCallSubSectionItems({
            vesselDetails,
            TEST_ID_BASE,
            LANG_BASE,
            t,
            transStyleClass,
            wrapperStyleClass,
            wrapperFirstStyleClass,
          }).map((col: any[], colIdx: number) => (
            <div className="flex flex-col flex-1" key={colIdx}>
              {col.map((item: any) => {
                const { testId, translate, text, textStyles, wrapperStyle, tranStyle } = item;
                return (
                  <PaperItem
                    key={testId}
                    testId={testId}
                    translate={translate}
                    textStyles={textStyles}
                    wrapperStyle={wrapperStyle}
                    tranStyle={tranStyle}
                    text={text}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </Paper>
      <Paper variant="outlined">
        <p className="font-bold mb-4">
          {t(`${LANG_BASE}.PreArrivalNotification.info-by-solas-isps-code`)}
        </p>
        <div className="grid grid-cols-2 gap-x-8">
          {getSolasIspsSubSectionItems({
            vesselDetails,
            TEST_ID_BASE,
            LANG_BASE,
            t,
            transStyleClass,
            wrapperStyleClass,
            wrapperFirstStyleClass,
          }).map((col: any[], colIdx: number) => (
            <div className="flex flex-col flex-1" key={colIdx}>
              {col.map((item: any) => {
                const { testId, translate, text, textStyles, wrapperStyle, tranStyle } = item;
                return (
                  <PaperItem
                    key={testId}
                    testId={testId}
                    translate={translate}
                    textStyles={textStyles}
                    wrapperStyle={wrapperStyle}
                    tranStyle={tranStyle}
                    text={text}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </Paper>
      <LastPortsTable rows={vesselDetails?.portCalls} />
      <div className="text-xl text-gray-500 mt-4">Certificates</div>
      <PreArrivalCertificates certificates={certificates} />
    </div>
  );
};

export default VesselPreArrivalNotification;
